import { useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../services/supabase';
import { AdminPageId, getAdminPageConfig } from '../config/adminPageConfig';

export interface AdminChartState {
  id: string;
  enabled: boolean;
  order: number;
}

export interface AdminSectionState {
  id: string;
  enabled: boolean;
  order: number;
  charts: AdminChartState[];
}

export interface AdminPageSettingsState {
  sections: AdminSectionState[];
}

const buildDefaultSettings = (pageId: AdminPageId): AdminPageSettingsState => {
  const config = getAdminPageConfig(pageId);
  if (!config) {
    return { sections: [] };
  }

  return {
    sections: config.sections.map((section, sectionIndex) => ({
      id: section.id,
      enabled: true,
      order: sectionIndex,
      charts: section.charts.map((chart, chartIndex) => ({
        id: chart.id,
        enabled: true,
        order: chartIndex
      }))
    }))
  };
};

export const useAdminPageSettings = (pageId: AdminPageId, locationName?: string | null) => {
  const defaultSettings = useMemo(() => buildDefaultSettings(pageId), [pageId]);
  const [settings, setSettings] = useState<AdminPageSettingsState>(defaultSettings);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSettings(defaultSettings);
  }, [defaultSettings]);

  useEffect(() => {
    if (!locationName) return;
    const loadSettings = async () => {
      setLoading(true);
      const { data: sectionRows, error: sectionError } = await supabaseClient
        .from('admin_page_sections')
        .select('section_id, is_enabled, sort_order')
        .eq('location_name', locationName)
        .eq('page_id', pageId);

      const { data: chartRows, error: chartError } = await supabaseClient
        .from('admin_page_charts')
        .select('section_id, chart_id, is_enabled, sort_order')
        .eq('location_name', locationName)
        .eq('page_id', pageId);

      if (sectionError || chartError) {
        console.error('Error loading admin page settings:', sectionError || chartError);
        setLoading(false);
        return;
      }

      const nextSettings: AdminPageSettingsState = {
        sections: defaultSettings.sections.map((section) => {
          const sectionRow = sectionRows?.find((row) => row.section_id === section.id);
          return {
            ...section,
            enabled: sectionRow ? sectionRow.is_enabled : section.enabled,
            order: sectionRow && typeof sectionRow.sort_order === 'number' ? sectionRow.sort_order : section.order,
            charts: section.charts.map((chart) => {
              const chartRow = chartRows?.find(
                (row) => row.section_id === section.id && row.chart_id === chart.id
              );
              return {
                ...chart,
                enabled: chartRow ? chartRow.is_enabled : chart.enabled,
                order: chartRow && typeof chartRow.sort_order === 'number' ? chartRow.sort_order : chart.order
              };
            })
          };
        })
      };

      setSettings(nextSettings);
      setLoading(false);
    };

    loadSettings();
  }, [pageId, locationName, defaultSettings]);

  const getSectionState = (sectionId: string) => settings.sections.find((section) => section.id === sectionId);
  const getChartState = (sectionId: string, chartId: string) =>
    getSectionState(sectionId)?.charts.find((chart) => chart.id === chartId);

  const isSectionEnabled = (sectionId: string) => getSectionState(sectionId)?.enabled ?? true;
  const isChartEnabled = (sectionId: string, chartId: string) => getChartState(sectionId, chartId)?.enabled ?? true;

  const getSectionOrder = (sectionId: string) => getSectionState(sectionId)?.order ?? 0;
  const getChartOrder = (sectionId: string, chartId: string) => getChartState(sectionId, chartId)?.order ?? 0;

  const getSectionsInOrder = () =>
    [...settings.sections].sort((a, b) => a.order - b.order);

  const getChartsInOrder = (sectionId: string) => {
    const section = getSectionState(sectionId);
    if (!section) return [];
    return [...section.charts].sort((a, b) => a.order - b.order);
  };

  return {
    settings,
    loading,
    isSectionEnabled,
    isChartEnabled,
    getSectionOrder,
    getChartOrder,
    getSectionsInOrder,
    getChartsInOrder
  };
};
